
import React, { useState, useEffect } from 'react';
import { UserIcon, Loader2, AlertCircle, PencilLine } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

interface AvatarDisplayProps {
  displayImage: string | null;
  isLoading: boolean;
  error: string | null;
  triggerFileInput: () => void;
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
  displayImage,
  isLoading,
  error,
  triggerFileInput
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset image states when displayImage URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [displayImage]);
  
  const handleAvatarClick = () => {
    console.log("Avatar clicked, triggering file input");
    if (!isLoading) {
      triggerFileInput();
    }
  };
  
  return (
    <div className="flex flex-col">
      <div className="relative w-full max-w-[180px] aspect-square">
        {/* Outer border container */}
        <div className="absolute inset-0 border-2 border-gray-200 rounded-2xl p-4">
          {/* Inner avatar container */}
          <div
            className={`relative w-full h-full overflow-hidden bg-muted flex items-center justify-center rounded-xl ${isLoading ? 'cursor-wait' : 'cursor-pointer group'}`}
            onClick={handleAvatarClick}
            role="button"
            aria-label="Change profile picture"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleAvatarClick();
              }
            }}
          >
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 transition-opacity duration-300">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
            )}

            {!imageLoaded && displayImage && !imageError && (
              <Skeleton className="absolute inset-0 z-10" />
            )}

            {displayImage ? (
              <>
                <img
                  src={displayImage}
                  alt="Avatar preview"
                  className={`h-full w-full object-cover transition-all duration-300 ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={(e) => {
                    console.error('Error loading avatar preview');
                    setImageError(true);
                    e.currentTarget.src = '/lovable-uploads/default-profile-avatar.png';
                  }}
                />
              </>
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <UserIcon className="h-20 w-20 text-gray-400" />
              </div>
            )}
          </div>

          {/* Edit button in top-right corner */}
          <button
            onClick={handleAvatarClick}
            className="absolute top-6 right-6 bg-white rounded-full p-2.5 shadow-md hover:shadow-lg transition-shadow z-30"
            aria-label="Edit profile picture"
          >
            <PencilLine className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-3 py-2 w-full">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription className="text-xs">
            {error}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AvatarDisplay;
